import type { PrescribingAlertView } from '@wodi/shared';

// What each prescribing alert says (docs/plans/phase1-consultation.md §4.1). The server
// sends rule IDs, codes and numbers only; the words are composed here, so a stored alert
// never holds text a person typed. Allergy and line names come from what the screen
// already shows (the banner's allergies, the draft lines), looked up by ID.

export interface AlertWordsContext {
  /** Allergy ID → how the banner names it ("Examplamycin (rash)"). */
  readonly allergyNames: ReadonlyMap<string, string>;
  /** Draft line key → drug shown on that line. */
  readonly lineDrugs: ReadonlyMap<string, string>;
}

export interface AlertWords {
  readonly title: string;
  readonly detail: string | null;
}

const CANNOT_CHECK_REASONS: Record<string, string> = {
  formulary_item_missing: 'this drug is no longer in the formulary',
  dose_invalid: 'the dose is not a number Wodi can read',
  strength_invalid: 'the strength is not set up correctly in the formulary',
  units_not_convertible: 'the dose and the limit are in units that can’t be compared',
  ambiguous_ingredient: 'a dose in mg does not say which ingredient of this combination it means',
  as_needed_without_maximum: '"when needed" has no maximum number of doses a day',
  frequency_invalid: 'the frequency has no number of doses a day',
  rules_overlap: 'two dose rules apply at once; ask the clinical lead to fix them',
  age_unknown: 'the limits depend on age and no date of birth is recorded',
  weight_outside_bands: 'no weight band covers this weight',
  other_line_not_computable: 'another medicine with the same ingredient can’t be added up',
};

const SEVERITY_WORDS: Record<string, string> = {
  mild: 'mild',
  moderate: 'moderate',
  severe: 'severe',
};

function text(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function provisional(alert: PrescribingAlertView): string {
  return alert.provisional ? ' Provisional: not yet approved by the clinical lead.' : '';
}

function source(alert: PrescribingAlertView): string {
  return alert.doseRule === null ? '' : ` Source: ${alert.doseRule.source}.`;
}

function ageNote(alert: PrescribingAlertView): string {
  return alert.facts['ageEstimated'] === true ? ' (estimated age)' : '';
}

function allergyList(alert: PrescribingAlertView, context: AlertWordsContext): string {
  return alert.evidence
    .flatMap((id) => {
      const name = context.allergyNames.get(id);
      return name === undefined ? [] : [name];
    })
    .join(', ');
}

/** The words for one alert. Never throws; an unknown fact reads as blank, never as a number. */
export function alertWords(alert: PrescribingAlertView, context: AlertWordsContext): AlertWords {
  const f = alert.facts;
  const drug =
    alert.lineKey === null ? 'This drug' : (context.lineDrugs.get(alert.lineKey) ?? 'This drug');
  const unit = text(f['unit']);
  switch (alert.ruleId) {
    case 'rx.allergy.severe': {
      const allergy = context.allergyNames.get(text(f['allergyId'])) ?? 'a recorded allergy';
      const why = f['reactionSeverity'] === 'severe' ? 'severe reaction' : 'high criticality';
      return {
        title: `Allergy: ${text(f['matchedDisplay'])}`,
        detail: `${drug} contains or belongs to ${text(f['matchedDisplay'])}. Recorded allergy: ${allergy}, ${why}.${provisional(alert)}`,
      };
    }
    case 'rx.allergy.match': {
      const allergy = context.allergyNames.get(text(f['allergyId'])) ?? 'a recorded allergy';
      const severity = SEVERITY_WORDS[text(f['reactionSeverity'])] ?? 'severity not recorded';
      return {
        title: `Allergy recorded to ${text(f['matchedDisplay'])} (${allergy}, ${severity}).`,
        detail: `Acknowledge with a reason or choose another drug.${provisional(alert)}`,
      };
    }
    case 'rx.allergy.uncoded':
      return {
        title: `Wodi can't check these recorded allergies against drugs: ${allergyList(alert, context) || 'allergies recorded in words only'}.`,
        detail: 'Check each drug yourself, or code the allergy.',
      };
    case 'rx.allergy.not_matchable':
      return {
        title: `Wodi can't check ${drug} against this patient's allergies: it has no ingredients in the formulary.`,
        detail: 'Check it yourself.',
      };
    case 'rx.allergy.not_recorded':
      return {
        title: 'Allergies not recorded.',
        detail: 'Ask the patient, then record allergies or No known allergies.',
      };
    case 'rx.duplicate.ingredient':
      return {
        title: `${text(f['substanceDisplay'])} is already prescribed: ${text(f['otherDisplay'])}${f['otherKind'] === 'course' ? ', a course still running' : f['otherKind'] === 'visit' ? ', earlier in this visit' : ', on this prescription'}.`,
        detail: null,
      };
    case 'rx.duplicate.group':
      return {
        title: `${drug} and ${text(f['otherDisplay'])} are both ${text(f['groupDisplay'])}.`,
        detail: provisional(alert).trim() || null,
      };
    case 'rx.dose.max_single':
      return {
        title: `${text(f['subjectDisplay'])} ${text(f['amount'])} ${unit} per dose is above the maximum of ${text(f['limit'])} ${unit}${ageNote(alert)}.`,
        detail: `${source(alert)}${provisional(alert)}`.trim() || null,
      };
    case 'rx.dose.max_daily':
      return {
        title: `${text(f['subjectDisplay'])} ${text(f['amount'])} ${unit} a day is above the maximum of ${text(f['limit'])} ${unit} a day${ageNote(alert)}.`,
        detail: `${source(alert)}${provisional(alert)}`.trim() || null,
      };
    case 'rx.dose.per_kg_high':
      return {
        title: `${text(f['perKg'])} ${unit}/kg per ${text(f['per'])} for ${text(f['weightKg'])} kg (weight today). Maximum ${text(f['limit'])} ${unit}/kg.`,
        detail: `${source(alert)}${provisional(alert)}`.trim() || null,
      };
    case 'rx.dose.per_kg_low':
      return {
        title: `Below the usual range for this weight: ${text(f['perKg'])} ${unit}/kg, usual from ${text(f['limit'])} ${unit}/kg.`,
        detail: null,
      };
    case 'rx.dose.weight_band': {
      const to = f['weightToKg'] === null ? 'and above' : `to under ${text(f['weightToKg'])} kg`;
      const low = text(f['bandLow']);
      const high = text(f['bandHigh']);
      const dose = low !== '' && high !== '' && low !== high ? `${low} to ${high}` : low || high;
      return {
        title: `For ${text(f['weightFromKg'])} kg ${to} the formulary dose is ${dose} ${unit}. This dose is ${text(f['amount'])} ${unit}.`,
        detail: `${source(alert)}${provisional(alert)}`.trim() || null,
      };
    }
    case 'rx.dose.no_weight':
      return {
        title: 'No weight recorded today. This drug’s dose depends on weight.',
        detail: 'Record the weight, then check again.',
      };
    case 'rx.dose.cannot_check':
      return {
        title: `Wodi couldn't check this dose against the limits: ${CANNOT_CHECK_REASONS[text(f['reason'])] ?? 'the limit can’t be applied'}.`,
        detail: 'Check the dose yourself.',
      };
    case 'rx.dose.no_rule':
      return { title: 'No dose limit set for this drug.', detail: null };
    case 'rx.estimated_age':
      return { title: 'The age band was chosen from an estimated age.', detail: null };
    case 'rx.formulary.not_stocked':
      return {
        title: f['state'] === 'not_stocked' ? 'Buy outside' : 'Out of stock here',
        detail: null,
      };
    case 'rx.quantity_short':
      return {
        title: `Quantity covers ${text(f['daysCovered'])} of ${text(f['courseDays'])} days.`,
        detail: null,
      };
  }
}

/** Always shown at the foot of the prescribing panel (plan §4.1, C24). */
export const INTERACTIONS_NOT_CHECKED =
  'Wodi checks allergies, duplicate medicines and dose limits set by your clinical lead. It does not check drug interactions.';
